import { useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Modal from "../../../../../../components/Modal";
import Spinner from "../../../../../../components/Loader";
import Button from "../../../../../../components/Button";
import { Center } from "../../../../../../components/StyledComponents";
import {
    Title,
    Text,
    CheckWordMatchInput,
    SubscriptionInfoItem,
} from "./styles";
import {
    cancelSubscription,
    getSubscription,
    resetCancelSubscription,
    resetGetSubscription,
} from "../../../../../../store/actions/subscription-admin";
import { PlanDto, Subscription } from "../../../../../../types";
import { hideModal } from "../../../../../../store/actions/modal";
import { getGroupSubscribers } from "../../../../../../store/actions/group";
import { Group } from "../../../../../../types/group";
import Snackbar from "../../../../../../components/Snackbar";
import dateHelpers from "../../../../../../helpers/dates-hanlder";
import { useTheme } from "styled-components";

interface ComponentProps {
    subscriptionId: number | null;
    subscription: Subscription | null;
    cancelSubscriptionStates: {
        loading: boolean;
        success: boolean;
        error: any;
    };
    subscriptionStates: {
        loading: boolean;
        success: boolean;
        error: string | boolean;
    };
    group: Group;
}

const CancelSubscriptionModal = ({
    subscriptionId,
    cancelSubscriptionStates,
    subscriptionStates,
    subscription,
    group,
}: ComponentProps) => {
    const dispatch = useDispatch();
    const [confirmationInput, setConfirmationInput] = useState("");
    const theme = useTheme() as { colors: { Primary: string } }

    const handleConfirm = () => {
        if (subscription) {
            dispatch(cancelSubscription({
                ...subscription,
                status: "expired",
            }));
        }
    };

    const paymentFrequencyTranslated = useMemo((): string => {
        if (!subscription?.plan) return "";

        if (subscription?.plan.type === "charge") return "1 sola vez";

        const unit: PlanDto["period_unit"] = subscription?.plan.period_unit;
        const period = subscription?.plan.period;

        if (unit === "month") {
            if (period === 1) return "mensual";
            if (period === 3) return "trimestral";
            if (period === 6) return "semestral";
            if (period === 12) return "anual";
            return `cada ${period} meses`;
        }

        if (unit === "year") {
            if (period === 1) return "anual";
            return `cada ${period} años`;
        }

        return `cada ${period} ${unit as PlanDto["period_unit"] === "month" ? "meses" : "años"}`;
    }, [subscription])

    const cancelationText = useMemo(() => {
        if (subscription?.type === "recurring") {
            const date = dateHelpers.fromUtcSecondsToDateStr(
                subscription?.current_term_end,
                "DD/MM/YYYY"
            );
            return date && date !== "Invalid date"
                ? `Al finalizar la suscripción (el ${date})`
                : "Al finalizar la suscripción";
        } else {
            return "Inmediatamente";
        }
    }, [subscription]);

    const confirmationText = useMemo(() => {
        const isGuest = subscription?.type === "guest";
        if (isGuest) {
            return `Al confirmar se removerá a ${subscription?.subscriber?.email} de tu ${group?.group_type === "membership" ? "Membresía" : "Taller"
                }. Esto incluye quitarle el acceso a todos los contenidos y expulsarlo de los grupos de chat.`;
        }
        return `Al dar de baja la suscripción se cancelará el débito automático y no se harán más cobros. Esta acción es irreversible. La persona tendrá acceso a los contenidos y grupos de chat hasta la fecha de vencimiento.`;
    }, [subscription?.type, subscription?.subscriber?.email, group?.group_type]);


    useEffect(() => {
        if (subscriptionId) {
            dispatch(getSubscription(subscriptionId));
        }
    }, [subscriptionId]);

    useEffect(() => {
        if (cancelSubscriptionStates.success) {
            dispatch(getGroupSubscribers({ page: 1, id: group?.id, filters: [] }));
            dispatch(hideModal());

            setTimeout(() => {
                dispatch(resetCancelSubscription());
            }, 5000);
        }
    }, [cancelSubscriptionStates.success]);

    const isGuest = subscription?.type === "guest";
    const isRecurring = subscription?.type === "recurring";
    const confirmationWord = "BAJA";

    return (
        <>
            <Modal name="expire-member-modal" width="600" showClose={true}>
                {subscriptionStates.success ? (
                    <>
                        <Title>
                            ⚠️ Estás por <strong><u>DAR DE BAJA</u></strong> la suscripción
                        </Title>
                        <div style={{ height: 16 }} />
                        <Text>
                            {confirmationText}
                        </Text>
                        <SubscriptionInfoItem>
                            <strong>Información de la suscripción:</strong>
                        </SubscriptionInfoItem>
                        <SubscriptionInfoItem>
                            Suscriptor: <i>{subscription?.subscriber?.email}</i>
                        </SubscriptionInfoItem>
                        <SubscriptionInfoItem>
                            Plan: <i>"{subscription?.plan?.external_name}" {!isGuest && `- ${subscription?.plan?.currency}${subscription?.current_amount} / ${paymentFrequencyTranslated}`}</i>
                        </SubscriptionInfoItem>
                        <SubscriptionInfoItem>
                            Cancelación:<i>{cancelationText}</i>
                        </SubscriptionInfoItem>

                        <div style={{ height: 30 }} />

                        <span>
                            Si estás seguro, escribe <strong>"{confirmationWord}"</strong> y luego toca en <strong>"CONFIRMAR"</strong>
                        </span>

                        <div style={{ height: 5 }} />

                        <CheckWordMatchInput
                            type="text"
                            placeholder="Escribe aquí la palabra..."
                            name="wordmatch"
                            style={{ marginBottom: 30 }}
                            onChange={(e) => setConfirmationInput(e.target.value.trim().toUpperCase())}
                        />

                        <Center horizontal="center" vertical="center" gap={12}>
                            <Button
                                options={{ type: "outline", skin: "purple", size: "lg", block: true }}
                                onClick={() => {
                                    dispatch(hideModal())
                                    dispatch(resetGetSubscription())
                                }}
                            >
                                Cancelar
                            </Button>

                            <Button
                                options={{ type: "filled", skin: "purple", size: "lg", block: true }}
                                disabled={confirmationInput !== confirmationWord}
                                onClick={handleConfirm}
                            >
                                <Center vertical="center" horizontal="center" gap={5}>
                                    {cancelSubscriptionStates.loading ? (
                                        <Spinner color="#fff" width={60} />
                                    ) : (
                                        <>Confirmar</>
                                    )}
                                </Center>
                            </Button>
                        </Center>
                    </>
                ) : (
                    <Center vertical="center" horizontal="center" column>
                        <p>Cargando información del suscriptor</p>
                        <Spinner color={theme?.colors?.Primary} width={60} />
                    </Center>
                )}
            </Modal>

            <Snackbar
                visible={cancelSubscriptionStates.success || !!cancelSubscriptionStates.error}
                options={{
                    time: 3000,
                    position: "center",
                    type: cancelSubscriptionStates.success ? "success" : "error",
                }}
                onHide={() => dispatch(resetCancelSubscription())}
            >
                {cancelSubscriptionStates.success
                    ? "Miembro cancelado exitosamente."
                    : "Se ha producido un error."}
            </Snackbar>
        </>
    );
};

const mapStateToProps = ({ groupStore, subscriptionAdminStore }) => {
    return {
        cancelSubscriptionStates: subscriptionAdminStore.cancelSubscription.states,
        group: groupStore.group.data,
        subscription: subscriptionAdminStore.subscriptionAdmin.data,
        subscriptionStates: subscriptionAdminStore.subscriptionAdmin.states,
    };
};

export default connect(mapStateToProps)(CancelSubscriptionModal);
